import { useEffect, useState } from 'react';
import { Card, Row, Col, Descriptions, Avatar, Typography, Select, Form, Space, Button, Alert, Image } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, HomeOutlined, EnvironmentOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined, SaveOutlined, DownloadOutlined, EyeOutlined, FileImageOutlined, FilePdfOutlined } from '@ant-design/icons';
import AdminSidebar from '../Components/Sidebar';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetail, userStatusUpdate } from '../redux/slices/adminSlice';

const { Title } = Typography;

function UserDetail() {
    const [form] = Form.useForm();
    const { id } = useParams()
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const dispatch = useDispatch()
    useEffect(() => {
        dispatch(getUserDetail(id))
    }, [dispatch])
    const { userDetail } = useSelector((state) => state?.admin)

    const userData = userDetail?.data;
    const userDetailData = userData?.detail;

    const getInitials = () => {
        const firstName = userData?.first_name || '';
        const lastName = userData?.last_name || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    useEffect(() => {
        if (userData) {
            let showStatus = null
            if (userData?.is_verified == 1) {
                showStatus = 'Active'
            } else if (userData?.is_verified == 0) {
                showStatus = 'Inactive'
            }
            form.setFieldsValue({
                status : showStatus
            })
        }
    }, [userData, form])

    const onSubmit = async (values) => {
        setLoading(true);
        setAlertMessage("");
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            let newStatus = null
            if (values?.status == "Inactive") {
                newStatus = 0
            } else {
                newStatus = 1
            }

            const resultAction = await dispatch(userStatusUpdate({
                id: id,
                status: newStatus
            }));

            if (userStatusUpdate.fulfilled.match(resultAction)) {
                setAlertMessage(resultAction?.payload?.message || 'User updated successfully!');
                setAlertType('success');
                setTimeout(() => {
                    setAlertMessage('');
                }, 4000);
            } else {
                setAlertMessage(resultAction?.payload?.error || 'Failed to update user status');
                setAlertType('error');
            }
        } catch (error) {
            setAlertMessage('Failed to update user status');
            setAlertType('error');
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const isPDF = (url) => url?.toLowerCase().endsWith('.pdf');

    return (
        <>
            <AdminSidebar renderContent={
                <div style={{ padding: '0' }}>
                    {/* User Profile Header Card */}
                    <Card
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Row gutter={[24, 24]} align="middle">
                            <Col>
                                <Avatar
                                    size={80}
                                    style={{
                                        backgroundColor: '#CA9AE8',
                                        fontSize: '32px',
                                        fontWeight: 'bold',
                                    }}
                                    icon={<UserOutlined />}
                                >
                                    {userData && getInitials()}
                                </Avatar>
                            </Col>
                            <Col flex={1}>
                                <Title level={3} style={{ margin: 0, color: '#262626' }}>
                                    {userData ? `${userData.first_name} ${userData.last_name}` : 'Loading...'}
                                </Title>
                                <p style={{ margin: '8px 0 0 0', color: '#8c8c8c', fontSize: '16px' }}>
                                    User Profile Information
                                </p>
                            </Col>
                        </Row>
                    </Card>

                    {/* Personal Information Card */}
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <UserOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Personal Information
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
                            <Descriptions.Item label={<span><UserOutlined style={{ marginRight: 4 }} />Full Name</span>}>
                                {userData ? `${userData.first_name} ${userData.last_name}` : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><MailOutlined style={{ marginRight: 4 }} />Email</span>}>
                                {userData?.email || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><PhoneOutlined style={{ marginRight: 4 }} />Phone</span>}>
                                {userData?.phone || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><FileTextOutlined style={{ marginRight: 4 }} />Bio</span>}>
                                {userDetailData?.bio || 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Address Information Card */}
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <HomeOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Address Information
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
                            <Descriptions.Item label={<span><HomeOutlined style={{ marginRight: 4 }} />Address</span>}>
                                {userDetailData?.address || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><EnvironmentOutlined style={{ marginRight: 4 }} />City</span>}>
                                {userDetailData?.city || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><EnvironmentOutlined style={{ marginRight: 4 }} />State</span>}>
                                {userDetailData?.state || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><EnvironmentOutlined style={{ marginRight: 4 }} />Zip Code</span>}>
                                {userDetailData?.zipcode || 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <HomeOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Status
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        {alertMessage && (
                            <Alert
                                message={alertMessage}
                                type={alertType}
                                showIcon
                                icon={alertType === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                closable
                                onClose={() => setAlertMessage('')}
                                style={{
                                    marginBottom: 24,
                                    borderRadius: '8px',
                                }}
                            />
                        )}
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onSubmit}
                            autoComplete="off"
                            size="large"
                        >
                            <Form.Item
                                label={
                                    <span style={{ fontSize: '15px', fontWeight: 500 }}>
                                        Status
                                    </span>
                                }
                                name="status"
                                rules={[
                                    { required: true, message: 'Please select status' }
                                ]}
                            >
                                <Select
                                    placeholder="Select Status"
                                    optionFilterProp="label"
                                    style={{
                                        borderRadius: '8px',
                                    }}
                                    options={[
                                        {
                                            value: '1',
                                            label: (
                                                <Space>
                                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                                    Active
                                                </Space>
                                            )
                                        },
                                        {
                                            value: '0',
                                            label: (
                                                <Space>
                                                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                                    Inactive
                                                </Space>
                                            )
                                        },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                                <Space size="middle">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        icon={<SaveOutlined />}
                                        loading={loading}
                                        size="large"
                                        style={{
                                            backgroundColor: '#CA9AE8',
                                            borderColor: '#CA9AE8',
                                            borderRadius: '8px',
                                            paddingLeft: '32px',
                                            paddingRight: '32px',
                                            height: '42px',
                                            fontSize: '15px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Update Status
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <FileImageOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Documents
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        {userData?.userDocument?.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                {userData.userDocument.map((data, index) => {
                                    const pdf = isPDF(data?.image_url);

                                    // Ensure proper URL
                                    const fileUrl = data?.image_url?.startsWith('http')
                                        ? data.image_url
                                        : `https://${data?.image_url}`;

                                    return (
                                        <div key={data.id || index} style={{ textAlign: 'center' }}>
                                            
                                            {/* Type */}
                                            <p>{data?.type}</p>

                                            {/* Preview */}
                                            {pdf ? (
                                                <div
                                                    style={{
                                                        width: 120,
                                                        height: 120,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: '1px solid #eee',
                                                        borderRadius: '8px',
                                                        background: '#fafafa',
                                                    }}
                                                >
                                                    <FilePdfOutlined style={{ fontSize: 40, color: '#ff4d4f' }} />
                                                </div>
                                            ) : (
                                                <Image
                                                    src={fileUrl}
                                                    alt={data?.type || 'document'}
                                                    width={120}
                                                    height={120}
                                                    style={{
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        border: '1px solid #eee',
                                                    }}
                                                    preview={{
                                                        mask: <EyeOutlined />,
                                                    }}
                                                />
                                            )}

                                            {/* Buttons (for BOTH image + PDF) */}
                                            <div
                                                style={{
                                                    marginTop: 8,
                                                    display: 'flex',
                                                    gap: '8px',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {/* View */}
                                                <Button
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => window.open(fileUrl, '_blank')}
                                                >
                                                    View
                                                </Button>

                                                {/* Download */}
                                                {/* <Button
                                                    size="small"
                                                    icon={<DownloadOutlined />}
                                                    onClick={() => {
                                                        const link = document.createElement('a');
                                                        link.href = fileUrl;
                                                        link.download = data?.type || 'document';
                                                        link.click();
                                                    }}
                                                >
                                                    Download
                                                </Button> */}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p>No documents available</p>
                        )}
                    </Card>

                </div>
            } />
        </>
    );
}

export default UserDetail;